import { Tour, TourPoint, LoginResponse, PaginatedResponse, User, AdminUser, Flavour, Review, Booking } from '../types';
import { extractTourText } from '../utils/tourUtils';

// Base API configuration - reads from runtime env-config.js (set per server)
const API_BASE_URL = (window as any)._env_?.REACT_APP_API_URL || 'http://localhost:3001';

class ApiService {
  private authToken: string | null = null;
  private flavourId: string | null = null;

  // Authentication methods
  setAuthToken(token: string): void {
    this.authToken = token;
  }

  clearAuthToken(): void {
    this.authToken = null;
  }

  setFlavourId(flavourId: string | null): void {
    this.flavourId = flavourId;
  }

  getFlavourId(): string | null {
    return this.flavourId;
  }

  // Method to check if token is valid
  async validateToken(): Promise<boolean> {
    try {
      const response = await this.makeRequest('/admin/me');
      return response.success;
    } catch (error) {
      return false;
    }
  }

  // Helper method to make authenticated requests
  private async makeRequest(endpoint: string, options: RequestInit & { isFormData?: boolean } = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers: HeadersInit = {};
    
    // Only set Content-Type for non-FormData requests
    if (!options.isFormData) {
      headers['Content-Type'] = 'application/json';
    }
    
    // Add existing headers
    Object.assign(headers, options.headers);

    if (this.authToken) {
      (headers as any)['Authorization'] = `Bearer ${this.authToken}`;
    }

    if (this.flavourId) {
      (headers as any)['x-flavour-id'] = this.flavourId;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    // Remove our custom flag before sending
    delete (config as any).isFormData;

    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Admin Authentication
  async login(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await this.makeRequest('/admin/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (response.success && response.data?.authToken) {
        this.setAuthToken(response.data.authToken);
        return {
          success: true,
          message: response.message,
          data: {
            userId: response.data.adminId,
            email: response.data.email,
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            authToken: response.data.authToken,
          },
        };
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error) {
      throw error;
    }
  }

  // Set admin password (for first-time login)
  async setAdminPassword(adminId: number, password: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.makeRequest('/admin/set-password', {
        method: 'POST',
        body: JSON.stringify({ adminId, password }),
      });

      return {
        success: response.success,
        message: response.message || 'Password set successfully',
      };
    } catch (error) {
      throw error;
    }
  }

  // Check admin password status
  async checkAdminPasswordStatus(email: string): Promise<{ success: boolean; message: string; data?: { hasPassword: boolean; admin: any } }> {
    try {
      const response = await this.makeRequest('/admin/check-password-status', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      return {
        success: response.success,
        message: response.message || 'Status checked successfully',
        data: response.data,
      };
    } catch (error) {
      throw error;
    }
  }

  // Change admin password
  async changePassword(passwordData: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.makeRequest('/admin/change-password', {
        method: 'POST',
        body: JSON.stringify(passwordData),
      });

      if (response.statusCode === 200 || response.success === true) {
        return {
          success: true,
          message: response.message || 'Password changed successfully',
        };
      } else {
        throw new Error(response.message || 'Failed to change password');
      }
    } catch (error) {
      throw error;
    }
  }

  // Get current admin profile
  async getCurrentUser(): Promise<User> {
    const response = await this.makeRequest('/admin/profile');

    if (response.success) {
      return {
        id: response.data.id,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email,
        phone: response.data.phone || '',
        profileImageUrl: response.data.profileImageUrl || '',
        gender: 1,
        isVerified: response.data.isVerified,
        isBlocked: !response.data.isActive,
        isDeleted: response.data.isDeleted,
        role: response.data.role,
        flavourId: response.data.flavourId,
        flavourIds: response.data.flavourIds,
        createdAt: new Date(response.data.createdAt),
        updatedAt: new Date(response.data.updatedAt),
      };
    } else {
      throw new Error(response.message || 'Failed to fetch user data');
    }
  }

  // Get available flavours
  async getAvailableFlavours(): Promise<Flavour[]> {
    try {
      const response = await this.makeRequest('/admin/app/flavours');
      if (response.success) {
        const flavours = response.data.map((flavour: any) => ({
          id: flavour.id.toString(),
          name: flavour.name,
          description: flavour.description,
          packageName: flavour.packageName,
          platform: flavour.platform,
          appIconUrl: flavour.appIconUrl,
          title: flavour.title,
          onboardBgUrl: flavour.onboardBgUrl,
          homeBgUrl: flavour.homeBgUrl,
          themeColors: flavour.themeColors,
          status: flavour.status,
          stepsDone: flavour.stepsDone,
          pipelineId: flavour.pipelineId,
          iosPipelineId: flavour.iosPipelineId,
          buildId: flavour.buildId,
          buildStatus: flavour.buildStatus,
          iosBuildId: flavour.iosBuildId,
          iosBuildStatus: flavour.iosBuildStatus,
          // Firebase Configuration fields
          keystoreSha1: flavour.keystoreSha1,
          keystoreSha256: flavour.keystoreSha256,
          googleServicesJsonUrl: flavour.googleServicesJsonUrl,
          googleServicesPlistUrl: flavour.googleServicesPlistUrl,
          googleClientApiAndroid: flavour.googleClientApiAndroid,
          googleClientApiIos: flavour.googleClientApiIos,
          mobileProvisionName: flavour.mobileProvisionName,
          mobileProvisionUUID: flavour.mobileProvisionUUID,
          // Keystore fields
          keystoreUrl: flavour.keystoreUrl,
          keystorePassword: flavour.keystorePassword,
          keystoreAlias: flavour.keystoreAlias,
          // Version fields
          androidVersionCode: flavour.androidVersionCode,
          androidVersionName: flavour.androidVersionName,
          iosVersionCode: flavour.iosVersionCode,
        }));
        return flavours;
      } else {
        throw new Error(response.message || 'Failed to fetch flavours');
      }
    } catch (error) {
      return [];
    }
  }



  // Get all admin users (all admins can see this)
  async getAdminUsers(
    page: number = 1,
    limit: number = 10,
  ): Promise<{ users: AdminUser[]; total: number; page: number; limit: number }> {
    try {
      const response = await this.makeRequest(`/admin/all?page=${page}&limit=${limit}`);
      if (response.success) {
        const adminsArray = response.admins || response.data?.admins || [];
        const total = response.total || response.data?.total || 0;
        const currentPage = response.page || response.data?.page || page;
        const currentLimit = response.limit || response.data?.limit || limit;
  
        const mappedUsers: AdminUser[] = adminsArray.map((admin: any) => ({
          id: admin.id,
          firstName: admin.firstName,
          lastName: admin.lastName,
          email: admin.email,
          phone: admin.phone || '',
          profileImageUrl: admin.profileImageUrl || '',
          gender: 1,
          isVerified: admin.isVerified,
          isBlocked: !admin.isActive,
          isDeleted: admin.isDeleted,
          role: admin.role,
          flavourId: admin.flavourId,
          flavourIds: admin.flavourIds,
          permissions: admin.permissions || ['read', 'write'],
          isActive: admin.isActive,
          lastLoginAt: admin.lastLoginAt ? new Date(admin.lastLoginAt) : undefined,
          createdAt: new Date(admin.createdAt),
          updatedAt: new Date(admin.updatedAt),
          createdBy: admin.createdBy,
          flavours: admin.flavours,
        }));
  
        return {
          users: mappedUsers,
          total,
          page: currentPage,
          limit: currentLimit,
        };
      } else {
        throw new Error(response.message || 'Failed to fetch admin users');
      }
    } catch (error) {
      throw error;
    }
  }

  // Get all users (super admin only)
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await this.makeRequest('/admin/users');
      if (response.success) {
        // Handle the new response structure with pagination
        const users = response.data.users || response.data;
        return users.map((user: any) => ({
          id: user.id,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email,
          phone: user.phone || '',
          profileImageUrl: user.profileImageUrl || '',
          gender: user.gender || 1,
          isVerified: user.isVerified,
          isBlocked: user.isBlocked || false,
          isDeleted: user.isDeleted || false,
          role: user.role || 'user',
          assignedTours: user.assignedTours || [],
          createdAt: user.createdAt ? new Date(user.createdAt) : new Date(),
          updatedAt: user.updatedAt ? new Date(user.updatedAt) : new Date(),
        }));
      } else {
        throw new Error(response.message || 'Failed to fetch users');
      }
    } catch (error) {
      throw error;
    }
  }

  // Create admin user (super admin only)
  async createAdminUser(userData: any): Promise<User> {
    try {
      const response = await this.makeRequest('/admin/create', {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      if (response.success) {
        return {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          phone: response.data.phone || '',
          profileImageUrl: response.data.profileImageUrl || '',
          gender: 1,
          isVerified: response.data.isVerified,
          isBlocked: !response.data.isActive,
          isDeleted: response.data.isDeleted,
          role: response.data.role,
          flavourId: response.data.flavourId,
          flavourIds: response.data.flavourIds,
          createdAt: new Date(response.data.createdAt),
          updatedAt: new Date(response.data.updatedAt),
        };
      } else {
        throw new Error(response.message || 'Failed to create admin user');
      }
    } catch (error) {
      throw error;
    }
  }

  // Update admin user
  async updateAdminUser(userId: number, userData: any): Promise<User> {
    try {
      const response = await this.makeRequest(`/admin/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });

      if (response.success) {
        return {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          phone: response.data.phone || '',
          profileImageUrl: response.data.profileImageUrl || '',
          gender: 1,
          isVerified: response.data.isVerified,
          isBlocked: !response.data.isActive,
          isDeleted: response.data.isDeleted,
          role: response.data.role,
          flavourId: response.data.flavourId,
          flavourIds: response.data.flavourIds,
          createdAt: new Date(response.data.createdAt),
          updatedAt: new Date(response.data.updatedAt),
        };
      } else {
        throw new Error(response.message || 'Failed to update admin user');
      }
    } catch (error) {
      throw error;
    }
  }

  // Delete admin user
  async deleteAdminUser(userId: number): Promise<void> {
    try {
      const response = await this.makeRequest(`/admin/${userId}`, {
        method: 'DELETE',
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete admin user');
      }
    } catch (error) {
      throw error;
    }
  }

  // Update admin user status
  async updateAdminUserStatus(userId: number, statusData: { isActive?: boolean }): Promise<User> {
    try {
      const response = await this.makeRequest(`/admin/${userId}/status`, {
        method: 'PUT',
        body: JSON.stringify(statusData),
      });

      if (response.success) {
        return {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          phone: response.data.phone || '',
          profileImageUrl: response.data.profileImageUrl || '',
          gender: 1,
          isVerified: response.data.isVerified,
          isBlocked: !response.data.isActive,
          isDeleted: response.data.isDeleted,
          role: response.data.role,
          createdAt: new Date(response.data.createdAt),
          updatedAt: new Date(response.data.updatedAt),
        };
      } else {
        throw new Error(response.message || 'Failed to update admin user status');
      }
    } catch (error) {
      throw error;
    }
  }



  // Booking management methods
  async getBookings(
    page: number = 1,
    limit: number = 10,
    filters?: {
      filterUser?: string;
      filterTour?: string;
      filterType?: 'playstore' | 'appstore' | 'bokun' | 'redeem' | 'all';
      userId?: number;
      tourId?: number;
      bundleId?: number;
    }
  ): Promise<{ bookings: Booking[]; total: number; page: number; limit: number; totalPages: number }> {
    try {
      const flavourId = this.getFlavourId() || localStorage.getItem('flavourId');
      if (!flavourId) {
        return { bookings: [], total: 0, page, limit, totalPages: 0 };
      }

      let url = `/admin/bookings?page=${page}&limit=${limit}`;
      
      if (filters) {
        if (filters.filterUser) {
          url += `&filterUser=${encodeURIComponent(filters.filterUser)}`;
        }
        if (filters.filterTour) {
          url += `&filterTour=${encodeURIComponent(filters.filterTour)}`;
        }
        if (filters.filterType && filters.filterType !== 'all') {
          url += `&filterType=${filters.filterType}`;
        }
        if (filters.userId) {
          url += `&userId=${filters.userId}`;
        }
        if (filters.tourId) {
          url += `&tourId=${filters.tourId}`;
        }
        if (filters.bundleId) {
          url += `&bundleId=${filters.bundleId}`;
        }
      }

      const response = await this.makeRequest(url);
      
      if (response.success && response.data) {
        return {
          bookings: response.data.bookings || [],
          total: response.data.total || 0,
          page: response.data.page || page,
          limit: response.data.limit || limit,
          totalPages: response.data.totalPages || 0,
        };
      } else {
        throw new Error(response.message || 'Failed to fetch bookings');
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteBooking(bookingId: number): Promise<void> {
    const response = await this.makeRequest(`/admin/bookings/${bookingId}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete booking');
    }
  }

  // Bundle management methods
  async getBundles(
    page: number = 1,
    limit: number = 10,
    filterName?: string
  ): Promise<{ bundles: any[]; total: number; page: number; limit: number; totalPages: number }> {
    try {
      // Get flavourId for the request
      const flavourId = this.getFlavourId() || localStorage.getItem('flavourId');
      if (!flavourId) {
        return {
          bundles: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        };
      }

      let url = `/admin/bundles?flavourId=${flavourId}&page=${page}&limit=${limit}`;
      if (filterName && filterName.trim() !== '') {
        url += `&filterName=${encodeURIComponent(filterName.trim())}`;
      }

      const response = await this.makeRequest(url);
      
      // Handle paginated response structure
      if (response.success && response.data) {
        return {
          bundles: response.data.bundles || [],
          total: response.data.total || 0,
          page: response.data.page || page,
          limit: response.data.limit || limit,
          totalPages: response.data.totalPages || 0,
        };
      } else if (Array.isArray(response)) {
        // Fallback for direct array response (backward compatibility)
        return {
          bundles: response,
          total: response.length,
          page: 1,
          limit: response.length,
          totalPages: 1,
        };
      } else {
        throw new Error(response.message || 'Failed to fetch bundles');
      }
    } catch (error) {
      return {
        bundles: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      };
    }
  }

  async getAvailableToursForBundle(): Promise<any[]> {
    try {
      // Get flavourId for the request
      const flavourId = this.getFlavourId() || localStorage.getItem('flavourId');
      if (!flavourId) {
        return [];
      }

      const response = await this.makeRequest(`/admin/bundles/tours?flavourId=${flavourId}`);
      
      // Handle both wrapped and direct responses
      if (response.success) {
        return response.data || [];
      } else if (Array.isArray(response)) {
        // Direct array response from backend
        return response;
      } else {
        throw new Error(response.message || 'Failed to fetch available tours');
      }
    } catch (error) {
      return [];
    }
  }

  async createBundle(bundleData: any): Promise<any> {
    try {
      const formData = new FormData();
      
      // Fields that backend CreateBundleDto expects (must match admin-bundle.dto.ts)
      const bundleFields = [
        'name', 'description', 'place', 'productId', 'bokunId', 
        'retailPrice', 'sellingPrice', 'status', 'flavourId', 'countryId', 'placeId',
        'isDeleted'
      ];
      
      bundleFields.forEach(key => {
        const value = bundleData[key];
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, String(value));
        }
      });
      
      // tourIds - special handling (JSON array)
      if (bundleData.tourIds && Array.isArray(bundleData.tourIds)) {
        formData.append('tourIds', JSON.stringify(bundleData.tourIds));
      }
      
      // Add thumbnail file (backend expects 'imageUrl' field name)
      if (bundleData.thumbnail) {
        formData.append('imageUrl', bundleData.thumbnail);
      }

      const response = await this.makeRequest('/admin/bundles', {
        method: 'POST',
        body: formData,
        isFormData: true,
      });

      // Handle both wrapped and direct responses
      if (response.success) {
        return response.data;
      } else if (response.id) {
        // Direct response from backend (no success wrapper)
        return response;
      } else {
        throw new Error(response.message || 'Failed to create bundle');
      }
    } catch (error) {
      throw error;
    }
  }

  async updateBundle(bundleId: number, bundleData: any): Promise<any> {
    try {
      const formData = new FormData();
      
      // Fields that backend UpdateBundleDto expects (must match admin-bundle.dto.ts)
      const bundleFields = [
        'name', 'description', 'place', 'productId', 'bokunId', 'bundleEmailTemplate',
        'retailPrice', 'sellingPrice', 'status', 'flavourId', 'countryId', 'placeId',
        'isDeleted', 'removeImage'
      ];
      
      bundleFields.forEach(key => {
        const value = bundleData[key];
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, String(value));
        }
      });
      
      // tourIds - special handling (JSON array)
      if (bundleData.tourIds !== undefined) {
        formData.append('tourIds', JSON.stringify(Array.isArray(bundleData.tourIds) ? bundleData.tourIds : []));
      }
      
      // Add thumbnail file (backend expects 'imageUrl' field name)
      if (bundleData.thumbnail) {
        formData.append('imageUrl', bundleData.thumbnail);
      }

      const response = await this.makeRequest(`/admin/bundles/${bundleId}`, {
        method: 'PUT',
        body: formData,
        isFormData: true,
      });

      // Handle both wrapped and direct responses
      if (response.success) {
        return response.data;
      } else if (response.id) {
        // Direct response from backend (no success wrapper)
        return response;
      } else {
        throw new Error(response.message || 'Failed to update bundle');
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteBundle(bundleId: number): Promise<void> {
    try {
      // Get flavourId for the request
      const flavourId = this.getFlavourId() || localStorage.getItem('flavourId');
      if (!flavourId) {
        throw new Error('No flavourId available for delete operation');
      }

      const response = await this.makeRequest(`/admin/bundles/${bundleId}?flavourId=${flavourId}`, {
        method: 'DELETE',
      });

      // Handle response from backend
      if (response.success === false) {
        throw new Error(response.message || 'Failed to delete bundle');
      } else {
        // If success or no success property, treat as success
        return;
      }
    } catch (error) {
      throw error;
    }
  }

  // Dashboard methods
  async getDashboardStats(): Promise<any> {
    try {
      const response = await this.makeRequest('/admin/dashboard/stats');
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch dashboard stats');
      }
    } catch (error) {
      return {
        totalUsers: 0,
        totalTours: 0,
        totalRevenue: 0,
        totalBookings: 0,
        revenueData: [],
        toursVsUsers: [],
        tourStatusDistribution: []
      };
    }
  }

  async downloadTour(tourId: number): Promise<void> {
    // Placeholder for tour download
  }

  // User Management
  async getUsers(
    page: number = 1,
    limit: number = 10,
    search?: string,
    status?: string,
  ): Promise<{ users: User[]; total: number; page: number; limit: number }> {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set('search', search);
      if (status && status !== 'all') params.set('status', status);
      const response = await this.makeRequest(`/admin/users?${params.toString()}`);
      if (response.success) {
        const { users, total, page: currentPage, limit: currentLimit } = response.data;

        const mappedUsers: User[] = users.map((user: any) => ({
          id: user.id,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email,
          phone: user.phone || '',
          profileImageUrl: user.profileImageUrl || '',
          gender: user.gender || 1,
          isVerified: user.isVerified,
          isBlocked: user.isBlocked || false,
          isDeleted: user.isDeleted || false,
          role: user.role || 'user',
          assignedTours: user.assignedTours || [],
          createdAt: user.createdAt ? new Date(user.createdAt) : new Date(),
          updatedAt: user.updatedAt ? new Date(user.updatedAt) : new Date(),
        }));

        return {
          users: mappedUsers,
          total,
          page: currentPage,
          limit: currentLimit,
        };
      } else {
        throw new Error(response.message || 'Failed to fetch users');
      }
    } catch (error) {
      throw error;
    }
  }

  async getToursForAssignment(
    page: number = 1,
    limit: number = 15,
  ): Promise<{ tours: Array<{ id: number; title: string; name: string; place?: string }>; totalPages: number }> {
    try {
      const flavourId = this.flavourId;
      if (!flavourId) {
        return { tours: [], totalPages: 1 };
      }
      const response = await this.makeRequest(`/admin/tours?flavourId=${flavourId}&page=${page}&limit=${limit}`);
      if (response.success) {
        const toursData = response.data?.tours || response.data?.data || response.data || [];
        const totalPages = response.data?.totalPages ?? 1;

        const tours = toursData.map((tour: any) => {
          const tourName = tour.name;
          const extractedTitle = typeof tourName === 'string'
            ? (tourName.startsWith('[') ? extractTourText(tourName) : tourName)
            : extractTourText(tourName);
          return {
            id: tour.id,
            title: extractedTitle,
            name: tour.name,
            place: tour.place,
          };
        });

        return { tours, totalPages };
      } else {
        throw new Error(response.message || 'Failed to fetch tours');
      }
    } catch (error) {
      return { tours: [], totalPages: 1 };
    }
  }

  async createUser(userData: { firstName: string; lastName: string; email: string; phone: string; assignedTours?: number[] }): Promise<User> {
    try {
      const response = await this.makeRequest('/admin/users', {
        method: 'POST',
        body: JSON.stringify(userData),
      });

      if (response.success) {
        return {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          phone: response.data.phone || '',
          profileImageUrl: response.data.profileImageUrl || '',
          gender: 1,
          isVerified: response.data.isVerified,
          isBlocked: response.data.isBlocked || false,
          isDeleted: response.data.isDeleted || false,
          role: 'user',
          assignedTours: response.data.assignedTours || [],
          createdAt: new Date(response.data.createdAt),
          updatedAt: new Date(response.data.updatedAt),
        };
      } else {
        throw new Error(response.message || 'Failed to create user');
      }
    } catch (error) {
      throw error;
    }
  }

  async updateUser(userId: number, userData: Partial<User>): Promise<User> {
    try {
      const response = await this.makeRequest(`/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });

      if (response.success) {
        return {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          phone: response.data.phone || '',
          profileImageUrl: response.data.profileImageUrl || '',
          gender: 1,
          isVerified: response.data.isVerified,
          isBlocked: response.data.isBlocked || false,
          isDeleted: response.data.isDeleted || false,
          role: 'user',
          assignedTours: response.data.assignedTours || [],
          createdAt: new Date(response.data.createdAt),
          updatedAt: new Date(response.data.updatedAt),
        };
      } else {
        throw new Error(response.message || 'Failed to update user');
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteUser(userId: number): Promise<void> {
    try {
      const response = await this.makeRequest(`/admin/users/${userId}`, {
        method: 'DELETE',
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      throw error;
    }
  }

  async updateUserStatus(userId: number, statusData: { isBlocked?: boolean; isVerified?: boolean }): Promise<User> {
    try {
      const response = await this.makeRequest(`/admin/users/${userId}/status`, {
        method: 'PUT',
        body: JSON.stringify(statusData),
      });

      if (response.success) {
        return {
          id: response.data.id,
          firstName: response.data.firstName,
          lastName: response.data.lastName,
          email: response.data.email,
          phone: response.data.phone || '',
          profileImageUrl: response.data.profileImageUrl || '',
          gender: 1,
          isVerified: response.data.isVerified,
          isBlocked: response.data.isBlocked || false,
          isDeleted: response.data.isDeleted || false,
          role: 'user',
          assignedTours: response.data.assignedTours || [],
          createdAt: new Date(response.data.createdAt),
          updatedAt: new Date(response.data.updatedAt),
        };
      } else {
        throw new Error(response.message || 'Failed to update user status');
      }
    } catch (error) {
      throw error;
    }
  }

  // Tour Management
  async getTours(
    name?: string,
    place?: string,
    page: number = 1,
    limit: number = 12
  ): Promise<PaginatedResponse<Tour>> {
    try {
      const flavourId = this.flavourId;
      if (!flavourId) {
        return { data: [], pagination: { page: 1, limit, total: 0, totalPages: 0 } };
      }

      const response = await this.makeRequest(
        `/admin/tours?flavourId=${flavourId}&filterName=${name || ''}&filterPlace=${place || ''}&page=${page}&limit=${limit}`
      );
      if (response.success) {
        // Handle paginated response structure
        const toursData = response.data?.tours || response.data || [];
        const total = response.data?.total || toursData.length || 0;
        const currentPage = response.data?.page || page;
        const currentLimit = response.data?.limit || limit;
        const totalPages = response.data?.totalPages || Math.ceil(total / currentLimit);
  
        return {
          data: toursData,
          pagination: {
            page: currentPage,
            limit: currentLimit,
            total: total,
            totalPages: totalPages
          }
        };
      } else {
        throw new Error(response.message || 'Failed to fetch tours');
      }
    } catch (error) {
      return {
        data: [],
        pagination: {
          page: 1,
          limit: 12,
          total: 0,
          totalPages: 0
        }
      };
    }
  }

  async createTour(tourData: Partial<Tour>): Promise<Tour> {
    try {
      // Create FormData for file uploads
      const formData = new FormData();
      
      // Add tour data fields
      Object.keys(tourData).forEach(key => {
        const value = (tourData as any)[key];
        if (value !== undefined && value !== null && key !== 'image' && key !== 'thumbnail' && key !== 'directionJson') {
          // Handle name and description arrays specially - send as JSON string for backend to parse
          if ((key === 'name' || key === 'description') && Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } 
          // Handle other arrays/objects by stringifying them
          else if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Add files if they exist
      if ((tourData as any).image) {
        formData.append('image', (tourData as any).image);
      }
      if ((tourData as any).thumbnail) {
        formData.append('thumbnail', (tourData as any).thumbnail);
      }
      if ((tourData as any).directionJson) {
        formData.append('directionJson', (tourData as any).directionJson);
      }

      const response = await this.makeRequest('/admin/tours/create', {
        method: 'POST',
        body: formData,
        isFormData: true,
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTour(tourId: number, tourData: Partial<Tour>): Promise<Tour> {
    try {
      // Create FormData for file uploads
      const formData = new FormData();
      
      // Add tour data fields
      Object.keys(tourData).forEach(key => {
        const value = (tourData as any)[key];
        if (value !== undefined && value !== null && key !== 'image' && key !== 'thumbnail' && key !== 'directionJson') {
          // Handle name and description arrays specially - send as JSON string for backend to parse
          if ((key === 'name' || key === 'description') && Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } 
          // Handle other arrays/objects by stringifying them
          else if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      // Add files if they exist
      if ((tourData as any).image) {
        formData.append('image', (tourData as any).image);
      }
      if ((tourData as any).thumbnail) {
        formData.append('thumbnail', (tourData as any).thumbnail);
      }
      if ((tourData as any).directionJson) {
        formData.append('directionJson', (tourData as any).directionJson);
      }

      const response = await this.makeRequest(`/admin/tours/${tourId}`, {
        method: 'PUT',
        body: formData,
        isFormData: true,
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTourById(tourId: number): Promise<Tour> {
    try {
      const response = await this.makeRequest(`/admin/tours/${tourId}`);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch tour');
      }
    } catch (error) {
      throw error;
    }
  }

  async deleteTour(tourId: number): Promise<void> {
    try {
      await this.makeRequest(`/admin/tours/${tourId}`, {
        method: 'DELETE',
      });
    } catch (error) {
      throw error;
    }
  }





  // Flavour/App Management Methods
  async createFlavour(flavourData: any): Promise<any> {
    const formData = new FormData();

    // Add all fields to FormData
    formData.append('name', flavourData.name);
    formData.append('description', flavourData.description || '');
    formData.append('packageName', flavourData.packageName);
    if (flavourData.title) {
      formData.append('title', flavourData.title);
    }

    // Add app icon if present
    if (flavourData.appIcon) {
      formData.append('files', flavourData.appIcon);
      formData.append('appIcon_type', 'appIcon');
    }

    // Add onboard background if present
    if (flavourData.onboardBg) {
      formData.append('files', flavourData.onboardBg);
      formData.append('onboardBg_type', 'onboardBg');
    }

    // Add home background if present
    if (flavourData.homeBg) {
      formData.append('files', flavourData.homeBg);
      formData.append('homeBg_type', 'homeBg');
    }

    // Add theme colors as JSON string
    if (flavourData.themeColors) {
      formData.append('themeColors', JSON.stringify(flavourData.themeColors));
    }

    const response = await this.makeRequest('/admin/app/flavours', {
      method: 'POST',
      body: formData,
      isFormData: true, // Custom flag to handle FormData differently
    });

    return response.data;
  }

  // Simplified app creation/update (auto-saves as draft or creates final app)
  async createOrUpdateFlavour(flavourData: any, isFinalSubmission: boolean = false): Promise<any> {
    try {
      const formData = new FormData();
      
      // Add all fields to FormData
      formData.append('name', flavourData.name);
      formData.append('description', flavourData.description || '');
      formData.append('packageName', flavourData.packageName);
      if (flavourData.title) {
        formData.append('title', flavourData.title);
      }
      
      // Add platform
      if (flavourData.platform) {
        // Ensure platform is a string, not an array or comma-separated
        let platformValue = flavourData.platform;
        if (Array.isArray(platformValue)) {
          platformValue = platformValue[0];
        } else if (typeof platformValue === 'string' && platformValue.includes(',')) {
          // Handle comma-separated strings like "android,android"
          platformValue = platformValue.split(',')[0].trim();
        }

        // Validate platform value
        if (!['android', 'ios', 'both'].includes(platformValue)) {
          platformValue = 'both'; // fallback
        }

        formData.append('platform', platformValue);
      }
      
      // Add step and status information
      formData.append('steps_done', flavourData.stepsDone || '1');
      formData.append('status', isFinalSubmission ? 'submit' : 'draft');

      // Add pipeline ID if present
      if (flavourData.pipelineId) {
        formData.append('pipelineId', flavourData.pipelineId);
      }
      if (flavourData.iosPipelineId) {
        formData.append('iosPipelineId', flavourData.iosPipelineId);
      }

      // Add Google Client API fields if present
      if (flavourData.googleClientApiAndroid) {
        formData.append('googleClientApiAndroid', flavourData.googleClientApiAndroid);
      }
      if (flavourData.googleClientApiIos) {
        formData.append('googleClientApiIos', flavourData.googleClientApiIos);
      }

      // Add iOS mobile provisioning profile metadata if present
      if (flavourData.mobileProvisionName) {
        formData.append('mobileProvisionName', flavourData.mobileProvisionName);
      }
      if (flavourData.mobileProvisionUUID) {
        formData.append('mobileProvisionUUID', flavourData.mobileProvisionUUID);
      }
      
      // Add app icon if present
      if (flavourData.appIcon) {
        formData.append('files', flavourData.appIcon);
        formData.append('appIcon_type', 'appIcon');
      }

      // Add onboard background if present
      if (flavourData.onboardBg) {
        formData.append('files', flavourData.onboardBg);
        formData.append('onboardBg_type', 'onboardBg');
      }

      // Add home background if present
      if (flavourData.homeBg) {
        formData.append('files', flavourData.homeBg);
        formData.append('homeBg_type', 'homeBg');
      }

      // Add Google Services JSON if present
      if (flavourData.googleServicesJson) {
        formData.append('files', flavourData.googleServicesJson);
        formData.append('googleServicesJson_type', 'googleServicesJson');
      }

      // Add Google Services Info.plist if present
      if (flavourData.googleServicesPlist) {
        formData.append('files', flavourData.googleServicesPlist);
        formData.append('googleServicesPlist_type', 'googleServicesPlist');
      }
      
      // Add theme colors as JSON string
      if (flavourData.themeColors) {
        formData.append('themeColors', JSON.stringify(flavourData.themeColors));
      }

      // Platform already added above with proper cleaning

      // Add keystore data if present (from separate keystore generation)
      if (flavourData.keystoreUrl) {
        formData.append('keystoreUrl', flavourData.keystoreUrl);
      }
      if (flavourData.keystoreSha1) {
        formData.append('keystoreSha1', flavourData.keystoreSha1);
      }
      if (flavourData.keystoreSha256) {
        formData.append('keystoreSha256', flavourData.keystoreSha256);
      }
      if (flavourData.keystorePassword) {
        formData.append('keystorePassword', flavourData.keystorePassword);
      }
      if (flavourData.keystoreAlias) {
        formData.append('keystoreAlias', flavourData.keystoreAlias);
      }
      if (flavourData.keystoreBuffer) {
        formData.append('keystoreBuffer', flavourData.keystoreBuffer);
      }

      // Add version fields if present
      if (flavourData.androidVersionCode) {
        formData.append('androidVersionCode', flavourData.androidVersionCode);
      }
      if (flavourData.androidVersionName) {
        formData.append('androidVersionName', flavourData.androidVersionName);
      }
      if (flavourData.iosVersionCode) {
        formData.append('iosVersionCode', flavourData.iosVersionCode);
      }

      const response = await this.makeRequest('/admin/app/flavours', {
        method: 'POST',
        body: formData,
        isFormData: true,
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Generate keystore and SHA fingerprints (returns buffer - no upload)
  async generateKeystore(packageName: string): Promise<{
    keystoreSha1: string;
    keystoreSha256: string;
    keystoreBuffer: string; // Base64 encoded buffer
    keystorePassword: string;
    keystoreAlias: string;
  }> {
    try {
      const response = await this.makeRequest('/admin/app/keystore/generate', {
        method: 'POST',
        body: JSON.stringify({ packageName }),
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }
  // Download keystore via proxy endpoint (ensures .keystore extension)
  async downloadKeystoreProxy(filePath: string): Promise<void> {
    try {
      const url = `${API_BASE_URL}/admin/app/keystore/download/${filePath}`;
      const headers: HeadersInit = {};
      
      if (this.authToken) {
        headers['Authorization'] = `Bearer ${this.authToken}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Get the blob and trigger download
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Extract filename from Content-Disposition header or derive from filePath
      const contentDisposition = response.headers.get('content-disposition');
      let filename: string | undefined;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      // Fallback: use package name from filePath (first segment) with .keystore extension
      if (!filename) {
        const pathParts = filePath.split('/');
        const packageName = pathParts[0] || 'keystore';
        filename = `${packageName}.keystore`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw error;
    }
  }

  // Trigger build for completed app
  async triggerBuild(flavourId: string, platform: 'android' | 'ios' = 'android'): Promise<any> {
    try {
      const response = await this.makeRequest(`/admin/app/flavours/${flavourId}/build`, {
        method: 'POST',
        body: JSON.stringify({ platform }),
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Pipeline ID related methods
  async triggerBuildWithPipeline(
    flavourId: string,
    pipelineId: string,
    platform: 'android' | 'ios' = 'android',
  ): Promise<any> {
    try {
      const response = await this.makeRequest(`/admin/app/flavours/${flavourId}/build`, {
        method: 'POST',
        body: JSON.stringify({ pipelineId, platform }),
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getBuildStatus(flavourId: string, buildId: string): Promise<any> {
    const response = await this.makeRequest(`/admin/app/flavours/${flavourId}/build/${buildId}/status`);
    return response.data;
  }

  // Check iOS build status
  async checkIosBuildStatus(flavourId: string): Promise<{ buildState: 'pending' | 'success' | 'failed'; azureStatus: string; azureResult: string | null }> {
    const response = await this.makeRequest(`/admin/app/flavours/${flavourId}/ios-build-status`);
    return response.data;
  }

  // Download .aab file
  async downloadAab(flavourId: string): Promise<void> {
    try {
      const url = `${API_BASE_URL}/admin/app/flavours/${flavourId}/download-aab`;
      const headers: HeadersInit = {};
      
      if (this.authToken) {
        headers['Authorization'] = `Bearer ${this.authToken}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      // Check if response is JSON (error response)
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download .aab file');
      }

      // If not JSON, it's a file download
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob and trigger download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'app.aab';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      throw error;
    }
  }

  async getAllFlavours(): Promise<Flavour[]> {
    try {
      const response = await this.makeRequest('/admin/app/flavours');
      if (response.success) {
                 return response.data.map((flavour: any) => ({
           id: flavour.id,
           name: flavour.name,
           description: flavour.description,
           packageName: flavour.packageName,
           appIconUrl: flavour.appIconUrl,
           title: flavour.title,
           onboardBgUrl: flavour.onboardBgUrl,
           homeBgUrl: flavour.homeBgUrl,
           themeColors: flavour.themeColors,
           status: flavour.status,
           logoUrl: flavour.logoUrl,
           websiteUrl: flavour.websiteUrl,
           contactEmail: flavour.contactEmail,
           contactPhone: flavour.contactPhone,
           pipelineId: flavour.pipelineId,
           iosPipelineId: flavour.iosPipelineId,
           buildId: flavour.buildId,
           buildStatus: flavour.buildStatus,
           iosBuildId: flavour.iosBuildId,
           iosBuildStatus: flavour.iosBuildStatus,
         }));
      } else {
        throw new Error(response.message || 'Failed to fetch flavours');
      }
    } catch (error) {
      return [];
    }
  }

  async getFlavourById(id: string): Promise<any> {
    try {
      const response = await this.makeRequest(`/admin/app/flavours/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateFlavour(id: string, flavourData: any): Promise<any> {
    try {
      const formData = new FormData();

      // Add all fields to FormData
      formData.append('name', flavourData.name);
      formData.append('description', flavourData.description || '');
      if (flavourData.title) {
        formData.append('title', flavourData.title);
      }

      // Add package name
      if (flavourData.packageName) {
        formData.append('packageName', flavourData.packageName);
      }

      // Add platform and steps done
      if (flavourData.platform) {
        // Ensure platform is a string, not an array or comma-separated
        let platformValue = flavourData.platform;
        if (Array.isArray(platformValue)) {
          platformValue = platformValue[0];
        } else if (typeof platformValue === 'string' && platformValue.includes(',')) {
          // Handle comma-separated strings like "android,android"
          platformValue = platformValue.split(',')[0].trim();
        }

        // Validate platform value
        if (!['android', 'ios', 'both'].includes(platformValue)) {
          platformValue = 'both'; // fallback
        }

        formData.append('platform', platformValue);
      }
      if (flavourData.stepsDone) {
        formData.append('steps_done', flavourData.stepsDone);
      }

      if (flavourData.pipelineId) {
        formData.append('pipelineId', flavourData.pipelineId);
      }
      if (flavourData.iosPipelineId) {
        formData.append('iosPipelineId', flavourData.iosPipelineId);
      }

      // Add iOS mobile provisioning profile metadata if present
      if (flavourData.mobileProvisionName) {
        formData.append('mobileProvisionName', flavourData.mobileProvisionName);
      }
      if (flavourData.mobileProvisionUUID) {
        formData.append('mobileProvisionUUID', flavourData.mobileProvisionUUID);
      }

      // Add app icon if present (for updates)
      if (flavourData.appIcon) {
        formData.append('files', flavourData.appIcon);
        formData.append('appIcon_type', 'appIcon');
      }

      // Add onboard background if present (for updates)
      if (flavourData.onboardBg) {
        formData.append('files', flavourData.onboardBg);
        formData.append('onboardBg_type', 'onboardBg');
      }

      // Add home background if present (for updates)
      if (flavourData.homeBg) {
        formData.append('files', flavourData.homeBg);
        formData.append('homeBg_type', 'homeBg');
      }
      
      // Add Google Services JSON if present
      if (flavourData.googleServicesJson) {
        formData.append('files', flavourData.googleServicesJson);
        formData.append('googleServicesJson_type', 'googleServicesJson');
      }

      // Add Google Services Info.plist if present
      if (flavourData.googleServicesPlist) {
        formData.append('files', flavourData.googleServicesPlist);
        formData.append('googleServicesPlist_type', 'googleServicesPlist');
      }
      
      // Add keystore data if present
      if (flavourData.keystoreUrl) {
        formData.append('keystoreUrl', flavourData.keystoreUrl);
      }
      if (flavourData.keystoreSha1) {
        formData.append('keystoreSha1', flavourData.keystoreSha1);
      }
      if (flavourData.keystoreSha256) {
        formData.append('keystoreSha256', flavourData.keystoreSha256);
      }
      if (flavourData.keystorePassword) {
        formData.append('keystorePassword', flavourData.keystorePassword);
      }
      if (flavourData.keystoreAlias) {
        formData.append('keystoreAlias', flavourData.keystoreAlias);
      }
      if (flavourData.keystoreBuffer) {
        formData.append('keystoreBuffer', flavourData.keystoreBuffer);
      }
      
      // Add theme colors as JSON string
      if (flavourData.themeColors) {
        formData.append('themeColors', JSON.stringify(flavourData.themeColors));
      }

      // Add version fields if present
      if (flavourData.androidVersionCode) {
        formData.append('androidVersionCode', flavourData.androidVersionCode);
      }
      if (flavourData.androidVersionName) {
        formData.append('androidVersionName', flavourData.androidVersionName);
      }
      if (flavourData.iosVersionCode) {
        formData.append('iosVersionCode', flavourData.iosVersionCode);
      }

      const response = await this.makeRequest(`/admin/app/flavours/${id}`, {
        method: 'PUT',
        body: formData,
        isFormData: true, // Custom flag to handle FormData differently
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteFlavour(id: string): Promise<void> {
    try {
      await this.makeRequest(`/admin/app/flavours/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      throw error;
    }
  }

  async checkPackageNameAvailability(packageName: string): Promise<boolean> {
    const response = await this.makeRequest(`/admin/app/flavours/check-package-name?packageName=${encodeURIComponent(packageName)}`);
    return response.data.isAvailable;
  }

  async updateFlavourDirectly(id: string, flavourData: any): Promise<any> {
    try {
      const response = await this.makeRequest(`/admin/app/flavours/${id}/update`, {
        method: 'PUT',
        body: JSON.stringify(flavourData),
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async approveFlavour(id: string): Promise<any> {
    try {
      const response = await this.makeRequest(`/admin/app/flavours/${id}/approve`, {
        method: 'POST',
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async rejectFlavour(id: string): Promise<any> {
    try {
      const response = await this.makeRequest(`/admin/app/flavours/${id}/reject`, {
        method: 'POST',
      });

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Tour Point (Tour Stop) Methods - Admin Endpoints
  async getTourPoints(tourId: number): Promise<TourPoint[]> {
    const response = await this.makeRequest(`/admin/tour-stops/tour/${tourId}`);
    if (response.success) {
      return response.data || [];
    } else {
      throw new Error(response.message || 'Failed to fetch tour points');
    }
  }

  async createTourPoint(pointData: any): Promise<TourPoint> {
    const response = await this.makeRequest('/admin/tour-stops', {
      method: 'POST',
      body: JSON.stringify(pointData),
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to create tour point');
    }
  }

  async createTourStopWithFiles(formData: FormData): Promise<TourPoint> {
    const response = await this.makeRequest('/admin/tour-stops/create-with-files', {
      method: 'POST',
      body: formData,
      isFormData: true,
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to create tour stop with files');
    }
  }



  async deleteTourPoint(pointId: number, tourId: number): Promise<void> {
    const response = await this.makeRequest(`/admin/tour-stops/tour/${tourId}/stop/${pointId}`, {
      method: 'DELETE',
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete tour point');
    }
  }

  async getTourPointById(tourId: number, pointId: number): Promise<TourPoint> {
    const response = await this.makeRequest(`/admin/tour-stops/tour/${tourId}/stop/${pointId}`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to fetch tour point');
    }
  }

  async bulkCreateTourStops(tourId: number, tourStopsData: any[]): Promise<TourPoint[]> {
    const response = await this.makeRequest(`/admin/tour-stops/tour/${tourId}/bulk`, {
      method: 'POST',
      body: JSON.stringify({ tourStops: tourStopsData }),
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to bulk create tour stops');
    }
  }

  async reorderTourStops(tourId: number, stopIds: number[]): Promise<TourPoint[]> {
    const response = await this.makeRequest(`/admin/tour-stops/tour/${tourId}/reorder`, {
      method: 'POST',
      body: JSON.stringify({ stopIds }),
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to reorder tour stops');
    }
  }

  async uploadTourStopFile(
    file: File, 
    tourId: number, 
    pointId: number, 
    fileType: 'audio' | 'image' | 'thumbnail', 
    language?: string
  ): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add language to FormData if provided (required for audio files to store in correct folder)
      // Try multiple field names in case backend expects a different name
      if (language) {
        formData.append('language', language);
        formData.append('lang', language); // Alternative field name
        formData.append('languageCode', language); // Another alternative
      }

      // Try adding language as query parameter as well (some backends prefer this)
      let endpoint = `/admin/tour-stops/tour/${tourId}/stop/${pointId}/upload/${fileType}`;
      if (language && fileType === 'audio') {
        endpoint += `?language=${language}`;
      }

      const response = await this.makeRequest(endpoint, {
        method: 'POST',
        body: formData,
        isFormData: true,
      });

      if (response.success) {
        return response.data.fileUrl;
      } else {
        throw new Error(response.message || 'Failed to upload tour stop file');
      }
    } catch (error) {
      throw error;
    }
  }

  // File Upload Methods
  async uploadImage(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Get the current flavour ID for the upload
      if (!this.flavourId) {
        throw new Error('Please create an app first before uploading files');
      }

      const response = await this.makeRequest('/admin/upload/image', {
        method: 'POST',
        body: formData,
        isFormData: true,
      });

      if (response.success) {
        return response.data.fileUrl;
      } else {
        throw new Error(response.message || 'Failed to upload image');
      }
    } catch (error) {
      throw error;
    }
  }

  async uploadAudio(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Get the current flavour ID for the upload
      if (!this.flavourId) {
        throw new Error('Please create an app first before uploading files');
      }

      const response = await this.makeRequest('/admin/upload/audio', {
        method: 'POST',
        body: formData,
        isFormData: true,
      });

      if (response.success) {
        return response.data.fileUrl;
      } else {
        throw new Error(response.message || 'Failed to upload audio');
      }
    } catch (error) {
      throw error;
    }
  }

  async uploadTourPointFile(file: File, tourId: number, pointId: number, fileType: 'audio' | 'image' | 'thumbnail'): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tourId', tourId.toString());
    formData.append('pointId', pointId.toString());
    formData.append('fileType', fileType);

    // Get the current flavour ID for the upload
    if (!this.flavourId) {
      throw new Error('Please create an app first before uploading files');
    }

    const response = await this.makeRequest('/admin/upload/tour-point-file', {
      method: 'POST',
      body: formData,
      isFormData: true,
    });

    if (response.success) {
      return response.data.fileUrl;
    } else {
      throw new Error(response.message || 'Failed to upload tour point file');
    }
  }

  // Update tour point
  async updateTourPoint(tourId: number, pointId: number, updateData: FormData | Partial<TourPoint>): Promise<TourPoint> {
    // Check if updateData is FormData
    const isFormData = updateData instanceof FormData;

    const response = await this.makeRequest(`/admin/tour-stops/tour/${tourId}/stop/${pointId}`, {
      method: 'PUT',
      body: isFormData ? updateData : JSON.stringify(updateData),
      isFormData: isFormData,
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to update tour point');
    }
  }



  // Enhanced Tour Point Operations
  async createTourStopWithValidation(pointData: Partial<TourPoint>): Promise<TourPoint> {
    const response = await this.makeRequest('/admin/tour-stops/create-with-validation', {
      method: 'POST',
      body: JSON.stringify(pointData),
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to create tour stop with validation');
    }
  }

  async validateTourStopData(pointData: Partial<TourPoint>): Promise<{
    isValid: boolean;
    errors: string[];
    warnings: string[];
  }> {
    const response = await this.makeRequest('/admin/tour-stops/validate', {
      method: 'POST',
      body: JSON.stringify(pointData),
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to validate tour stop data');
    }
  }

  async getAvailableTours(): Promise<{ id: number; name: string; description: string }[]> {
    const response = await this.makeRequest('/admin/tour-stops/available-tours');
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to fetch available tours');
    }
  }

  async getTourStopStats(tourId: number): Promise<{
    totalStops: number;
    activeStops: number;
    hiddenStops: number;
    deletedStops: number;
  }> {
    const response = await this.makeRequest(`/admin/tour-stops/tour/${tourId}/stats`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to fetch tour stop stats');
    }
  }

  // Azure DevOps Pipeline Management (via Backend)
  async getPipelineDefinition(pipelineId: string): Promise<any> {
    const response = await this.makeRequest(`/admin/app/pipeline/${pipelineId}/definition`, {
      method: 'GET',
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to get pipeline definition');
    }
  }

  async updatePipelineDefinition(pipelineId: string, definitionBody: any): Promise<any> {
    const response = await this.makeRequest(`/admin/app/pipeline/${pipelineId}/definition`, {
      method: 'PUT',
      body: JSON.stringify(definitionBody),
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to update pipeline definition');
    }
  }

  async updatePipelineVariables(
    pipelineId: string,
    packageName: string,
    androidPlayUpload?: boolean,
    platform: 'android' | 'ios' | 'both' = 'android'
  ): Promise<void> {
    const response = await this.makeRequest(`/admin/app/pipeline/${pipelineId}/variables`, {
      method: 'POST',
      body: JSON.stringify({
        packageName,
        androidPlayUpload,
        platform,
      }),
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update pipeline variables');
    }
  }

  // Country Management Methods
  async getCountries(): Promise<Array<{ id: number; name: string; code?: string; isActive: boolean }>> {
    try {
      const response = await this.makeRequest('/admin/countries');
      if (response.success) {
        return response.data || [];
      } else {
        throw new Error(response.message || 'Failed to fetch countries');
      }
    } catch (error) {
      return [];
    }
  }

  async createCountry(countryData: { name: string; code?: string }): Promise<{ id: number; name: string; code?: string }> {
    const response = await this.makeRequest('/admin/countries', {
      method: 'POST',
      body: JSON.stringify(countryData),
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to create country');
    }
  }


  // Place Management Methods
  async getPlaces(countryId?: number): Promise<Array<{ id: number; name: string; countryId: number; country?: { id: number; name: string } }>> {
    try {
      let url = '/admin/places';
      if (countryId) {
        url += `?countryId=${countryId}`;
      }
      const response = await this.makeRequest(url);
      if (response.success) {
        return response.data || [];
      } else {
        throw new Error(response.message || 'Failed to fetch places');
      }
    } catch (error) {
      return [];
    }
  }

  async createPlace(placeData: { name: string; countryId: number; description?: string }): Promise<{ id: number; name: string; countryId: number }> {
    const response = await this.makeRequest('/admin/places', {
      method: 'POST',
      body: JSON.stringify(placeData),
    });

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to create place');
    }
  }
  
  // FAQ Management
  async getAllFaq(type?: string, category?: string, tourId?: number): Promise<{ data: { data: any } }> {
    try {
      const params = new URLSearchParams();
      if (type) params.append('type', type);
      if (category) params.append('category', category);
      if (tourId) params.append('tourId', String(tourId));
      const qs = params.toString();
      const response = await this.makeRequest(`/faq/list${qs ? `?${qs}` : ''}`, {
        method: 'GET',
      });
      if (response.status !== undefined && response.data) {
        return { data: { data: response.data } };
      } else if (response.success && response.data) {
        return { data: { data: response.data } };
      } else if (Array.isArray(response)) {
        return { data: { data: response } };
      } else if (response.data) {
        return { data: { data: response.data } };
      }
      return { data: { data: [] } };
    } catch (error) {
      throw error;
    }
  }
  async postAddFaq(faqData: any): Promise<{ status: number; data: any }> {
    try {
      const payload: any = {
        type: faqData.type,
        category: faqData.category,
        name: faqData?.name,
        description: faqData?.description,
        topQuestion: faqData?.topQuestion,
        status: faqData?.status !== undefined ? faqData.status : true,
      };
      if (faqData?.tourId !== undefined && faqData?.tourId !== null) {
        payload.tourId = faqData.tourId;
      }
      const response = await fetch(`${API_BASE_URL}/faq/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.authToken || localStorage.getItem('authToken') || ''}`,
          ...(this.flavourId ? { 'x-flavour-id': this.flavourId } : {}),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      return { status: response.status, data };
    } catch (error) {
      throw error;
    }
  }
  // Review Management Methods
  async getReviews(
    flavourId: string,
    status?: 'PENDING' | 'APPROVED' | 'REJECTED',
    page: number = 1,
    limit: number = 10
  ): Promise<{ reviews: Review[]; total: number; page: number; limit: number; totalPages: number }> {
    try {
      let url = `/admin/reviews?flavourId=${flavourId}&page=${page}&limit=${limit}`;
      if (status) {
        url += `&status=${status}`;
      }

      const response = await this.makeRequest(url);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch reviews');
      }
    } catch (error) {
      throw error;
    }
  }

  async removeAddFaq(id: any): Promise<{ status: number; data: any }> {
    const response = await fetch(`${API_BASE_URL}/faq/delete/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authToken || localStorage.getItem('authToken') || ''}`,
        ...(this.flavourId ? { 'x-flavour-id': this.flavourId } : {}),
      },
    });
    const data = await response.json();
    return { status: response.status, data };
  }
  async getReviewById(reviewId: number, flavourId: string): Promise<Review> {
    const response = await this.makeRequest(`/admin/reviews/${reviewId}?flavourId=${flavourId}`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to fetch review');
    }
  }

  async editFaq(id: any, faqData: any): Promise<{ status: number; data: any }> {
    const payload: any = { ...faqData };
    // Preserve tourId when 0 (All Tours) or number; only strip if null/undefined
    if (payload.tourId === null || payload.tourId === undefined) {
      delete payload.tourId;
    }
    const response = await fetch(`${API_BASE_URL}/faq/update/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.authToken || localStorage.getItem('authToken') || ''}`,
        ...(this.flavourId ? { 'x-flavour-id': this.flavourId } : {}),
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    return { status: response.status, data };
  }

  async approveReview(reviewId: number, flavourId: string): Promise<void> {
    const response = await this.makeRequest(`/admin/reviews/${reviewId}/approve?flavourId=${flavourId}`, {
      method: 'PUT',
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to approve review');
    }
  }

  async rejectReview(reviewId: number, flavourId: string): Promise<void> {
    const response = await this.makeRequest(`/admin/reviews/${reviewId}/reject?flavourId=${flavourId}`, {
      method: 'PUT',
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to reject review');
    }
  }

  async deleteReview(reviewId: number, flavourId: string): Promise<void> {
    const response = await this.makeRequest(`/admin/reviews/${reviewId}?flavourId=${flavourId}`, {
      method: 'DELETE',
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete review');
    }
  }

  async updateReviewStatus(reviewId: number, status: 'PENDING' | 'APPROVED' | 'REJECTED', flavourId: string): Promise<void> {
    const response = await this.makeRequest(`/admin/reviews/${reviewId}/status?flavourId=${flavourId}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to update review status');
    }
  }

  async createReview(
    reviewData: {
      title?: string;
      userName: string;
      reviewDate: string;
      helpfulCount?: number;
      imageUrls?: string[];
      reviewSourceId?: number;
      tourId: number;
      review: string;
      rating: number;
      status?: 'PENDING' | 'APPROVED' | 'REJECTED';
    },
    flavourId: string,
    images?: File[],
  ): Promise<any> {
    try {
      // Create FormData for file uploads
      const formData = new FormData();

      // Add review data fields
      if (reviewData.title) {
        formData.append('title', reviewData.title);
      }
      formData.append('userName', reviewData.userName);
      formData.append('reviewDate', reviewData.reviewDate);
      if (reviewData.helpfulCount !== undefined) {
        formData.append('helpfulCount', reviewData.helpfulCount.toString());
      }
      if (reviewData.reviewSourceId !== undefined) {
        formData.append('reviewSourceId', reviewData.reviewSourceId.toString());
      }
      formData.append('tourId', reviewData.tourId.toString());
      formData.append('review', reviewData.review);
      formData.append('rating', reviewData.rating.toString());
      if (reviewData.status) {
        formData.append('status', reviewData.status);
      }

      // Add images if provided
      if (images && images.length > 0) {
        images.forEach((image) => {
          formData.append('images', image);
        });
      }

      const response = await this.makeRequest(`/admin/reviews?flavourId=${flavourId}`, {
        method: 'POST',
        body: formData,
        isFormData: true,
      });
      if (!response.success) {
        throw new Error(response.message || 'Failed to create review');
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getReviewSources(): Promise<Array<{ id: number; name: string; image?: string }>> {
    const response = await this.makeRequest('/admin/reviews/sources/list');
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to fetch review sources');
    }
  }

  async getAvailableToursForReview(flavourId: string): Promise<Array<{ id: number; name: string; description?: string }>> {
    const response = await this.makeRequest(`/admin/reviews/tours/available?flavourId=${flavourId}`);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Failed to fetch available tours');
    }
  }

  // Social Settings Methods

  async getSocialSettings(flavourId: string): Promise<any> {
    return await this.makeRequest(`/admin/flavours/${flavourId}/social`);
  }

  async createSocialSettings(
    flavourId: string,
    data: { instagramLink?: string; facebookLink?: string; hashtag?: string },
  ): Promise<any> {
    return await this.makeRequest(`/admin/flavours/${flavourId}/social`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSocialSettings(
    flavourId: string,
    data: { instagramLink?: string; facebookLink?: string; hashtag?: string },
  ): Promise<any> {
    return await this.makeRequest(`/admin/flavours/${flavourId}/social`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSocialSettings(flavourId: string): Promise<any> {
    return await this.makeRequest(`/admin/flavours/${flavourId}/social`, {
      method: 'DELETE',
    });
  }
}

// Export the class for use in service factory
export { ApiService };

const apiService = new ApiService();
export default apiService; 